import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;
const AUTH_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';

const getAccessToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const persistAuthTokens = (authPayload = {}) => {
  const accessToken = authPayload.access_token || authPayload.token || null;
  const refreshToken = authPayload.refresh_token || null;
  if (accessToken) {
    localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

export const clearAuthTokens = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

const api = axios.create({
  baseURL: API,
  timeout: 20000,
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshInFlight = null;
const refreshAccessToken = async () => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  refreshInFlight = axios
    .post(`${API}/auth/refresh`, { refresh_token: refreshToken })
    .then((response) => {
      persistAuthTokens(response.data || {});
      return response.data;
    })
    .catch((error) => {
      clearAuthTokens();
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error?.config || {};
    const status = error?.response?.status;
    const requestUrl = originalConfig?.url || '';
    const isAuthRoute = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register') || requestUrl.includes('/auth/refresh');

    if (status === 401 && !originalConfig._retry && !isAuthRoute) {
      originalConfig._retry = true;
      try {
        const refreshed = await refreshAccessToken();
        const token = refreshed?.access_token || refreshed?.token;
        if (!token) {
          throw new Error('Token refresh succeeded without access token');
        }
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${token}`;
        return api(originalConfig);
      } catch (refreshError) {
        clearAuthTokens();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    persistAuthTokens(response.data || {});
    return response;
  },
  register: async (email, password, name, role) => {
    const response = await api.post('/auth/register', { email, password, name, role });
    persistAuthTokens(response.data || {});
    return response;
  },
  me: () => api.get('/auth/me'),
  refresh: () => refreshAccessToken(),
  logout: async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refresh_token: refreshToken });
      } catch (error) {
      }
    }
    clearAuthTokens();
  },
  logoutAll: async () => {
    try {
      await api.post('/auth/logout-all');
    } finally {
      clearAuthTokens();
    }
  },
  getProfile: () => api.get('/profile'),
  updateProfile: (payload) => api.put('/profile', payload),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (jti) => api.delete(`/auth/sessions/${encodeURIComponent(jti)}`),
};

export const propertyAPI = {
  getAll: (params = {}) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
};

export const agentAPI = {
  parseListingPdf: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/agent/listings/parse-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  publishListingDraft: (draftId, data) =>
    api.post(`/agent/listings/drafts/${draftId}/publish`, data),
};

export const aiAPI = {
  chat: (message, sessionId = null, context = null) =>
    api.post('/ai/chat', { message, session_id: sessionId, context }),
  getConversations: () => api.get('/ai/conversations'),
  getConversation: (sessionId) => api.get(`/ai/conversations/${encodeURIComponent(sessionId)}`),
  valuation: (data) => api.post('/ai/valuation', data),
  generateImage: (prompt, propertyId = null) =>
    api.post('/ai/generate-image', { prompt, property_id: propertyId }),
};

export const marketAPI = {
  getInsights: (location = null, state = 'QLD', insightsMode = 'hybrid', dataSource = 'internal') =>
    api.get('/market/insights', { params: { location, state, insights_mode: insightsMode, data_source: dataSource } }),
  getSuggestions: (state = 'QLD', search = '') => api.get('/locations/suggest', { params: { state, search } }),
  getAvailableStates: () => api.get('/market/states'),
};

export const amenitiesAPI = {
  getNearbyAmenities: (latitude, longitude, radiusM = 1000) =>
    api.get(`/amenities/nearby/${latitude}/${longitude}`, { params: { radius_m: radiusM } }),
};

export const demographicsAPI = {
  getSuburbData: (suburb, state = 'QLD') =>
    api.get('/demographics/suburb', { params: { suburb, state } }),
  getRBARates: () => api.get('/demographics/rates/rba'),
  getLocationRisk: (latitude, longitude, state = 'QLD') =>
    api.get(`/demographics/risk/${latitude}/${longitude}`, { params: { state } }),
  getSchoolCatchment: (latitude, longitude, state = 'QLD') =>
    api.get(`/education/school-catchment/${latitude}/${longitude}`, { params: { state } }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const buyerAPI = {
  createProfile: (data) => api.post('/buyer/profile', data),
  getProfile: () => api.get('/buyer/profile'),
  getRecommendations: (limit = 20) => api.get('/buyer/recommendations', {
    params: { limit },
  }),
  getFinancialReality: (data) => api.post('/buyer-intelligence/financial-reality', data),
};

export const roadmapAPI = {
  generate: (data) => api.post('/roadmap/generate', data),
};

export const toolsAPI = {
  extractFloorPlan: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/tools/floor-plan/extract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export default api;

